'use client'

import { useEffect, useRef } from 'react'
import { UsePromptFormReturn } from '@/lib/types/prompt-area-types'
import { ContentFile } from '@/lib/types'
import { Plus } from 'lucide-react'
import { RingAndFadeAnimation } from '@/components/filesV2/file-card'
import { useSimpleFileUpload } from '@/hooks/useSimpleFileUpload'
import { Button } from '@/components/ui/button'
import { useDragDrop } from '@/components/providers/drag-drop-provider'
import { cn } from '@/lib/utils'

export function PromptAreaImageInline({
    promptForm
}: {
    promptForm: UsePromptFormReturn
}) {
    const shouldRender = promptForm.currentMode === 'image' || promptForm.currentSubMode?.label === 'Edit'
    const sourceImage = promptForm.form.watch('sourceImage') as ContentFile | undefined
    console.log('sourceImage', sourceImage)
    const files = promptForm.form.watch('files') as unknown as ContentFile[] | undefined
    const prevFilesCountRef = useRef<number>(files?.length || 0)

    useEffect(() => {
        const currentFiles = files || []
        if (currentFiles.length > prevFilesCountRef.current && !sourceImage && (promptForm.currentMode === 'image')) {
            const lastImage = [...currentFiles].reverse().find(f => f.mediaCategory === 'image')
            if (lastImage) {
                ;(promptForm.form.setValue as any)('sourceImage', lastImage)
            }
        }
        prevFilesCountRef.current = currentFiles.length
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files])


    const { openFileBrowser, uploadFiles, dragProps, isUploading } = useSimpleFileUpload({
        endpoint: 'singleImageUploader',
        maxFiles: 1,
        onUploadStart: (file) => {
            // console.log('\n\n\n\n\n\n\n\n\n\n\non upload start', file.uploadState.progress)
            promptForm.form.setValue('sourceImage', file)
        },
        onUploadProgress: (file) => {
            // console.log('on upload progress', file.uploadState.progress)
            promptForm.form.setValue('sourceImage', file)
        },
        onUploadComplete: (file) => {
            // console.log('on upload complete', file.uploadState.progress)
            promptForm.form.setValue('sourceImage', file)
        },
        onUploadError: (file) => {
            // console.log('on upload error', file.uploadState.progress)
            promptForm.form.setValue('sourceImage', file)
        },
    })

    // Wire global drag-drop to this inline uploader when in image mode
    const { setOnFilesDropped, isDragActive, setIsDragActive } = useDragDrop()
    const uploadFilesRef = useRef(uploadFiles)
    useEffect(() => { uploadFilesRef.current = uploadFiles }, [uploadFiles])
    useEffect(() => {
        if (promptForm.currentMode !== 'image') return
        const handler = (files: File[]) => {
            setIsDragActive(false)
            uploadFilesRef.current(files)
        }
        setOnFilesDropped(handler)
        return () => setOnFilesDropped(() => {})
    }, [promptForm.currentMode, setOnFilesDropped, setIsDragActive])

    const openPicker = () => {
        openFileBrowser()
    }

    if (!shouldRender) return null

    console.log('sourceImage', sourceImage?.uploadState?.progress)

    return (
        <div
            className={"w-[60px] h-[60px] flex-shrink-0 transition-colors"}
            onDrop={(e) => { setIsDragActive(false); dragProps.onDrop(e) }}
            onDragOver={(e) => { setIsDragActive(true); dragProps.onDragOver(e) }}
            onDragLeave={(e) => { setIsDragActive(false); dragProps.onDragLeave(e) }}
        >

            {sourceImage && !isDragActive ? (
                <Button
                    type="button"
                    variant="ghost"
                    className="w-full h-full rounded-md overflow-hidden hover:bg-accent"
                    onClick={openPicker}
                    aria-label="Change source image"
                >
                    <RingAndFadeAnimation file={sourceImage} iconSize={60} />
                </Button>
            ) : (
                <Button
                    type="button"
                    className={cn(
                        "w-full h-full rounded-md border-2 border-dashed border-border bg-transparent flex items-center justify-center text-muted-foreground hover:bg-accent",
                        isDragActive && 'border-primary/60 bg-primary/10'

                    )}
                    onClick={openPicker}
                    aria-label="Upload image"
                >
                    <div className="flex flex-col items-center gap-0 leading-none">
                        <Plus className={cn("h-5 w-5", isDragActive ? "text-primary/50" : "text-muted-foreground")} />
                        <span className={cn("text-[10px] mt-0.5", isDragActive ? "text-primary/50" : "text-muted-foreground")}>Upload</span>
                    </div>
                </Button>
            )}
        </div>
    )
}


